import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { debounce } from "lodash";
import moment from "moment-timezone";
import store from "@/store";
import ability from "@/libs/acl/ability";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { decryptData } from "@/utils/decryptUserDataUtils";
import axios from '@axios';

export default function useList(showFilters = true) {
  const refListTable = ref(null);
  const toast = useToast();

  const user = JSON.parse(decryptData(window.localStorage.userData));
  const analystUserId = user.id;

  store.dispatch("user/getAll", { max: 50 });
  const userOptions = computed(() => store.getters["user/getSelectedItems"] || []);

  const videoTours = ref([]);
  const getVideos = async () => {
    try {
      const { data } = await axios.get("v1/video-tours", { params: { per_page: 500 } });
      videoTours.value = data.data;
    } catch (error) {
      console.error(error);
    }
  };
  getVideos()
  const videoTourOptions = computed(() =>
    videoTours.value.map((tour) => ({
      value: tour.id,
      label: tour.name
    }))
  );

  const supervisorFilter = ref(null);
  const videoTourFilter = ref(null);
  const scheduledFilter = ref(null);

const tableColumns = [
  { label: "assigned_video_tours.analyst", key: "analyst", sortable: false, formatter: "formatUser" },
  { label: "assigned_video_tours.video_tours", key: "video_tours", sortable: false },
  { label: "assigned_video_tours.supervisor", key: "supervisor", sortable: false, formatter: "formatUser" },
  { label: "assigned_video_tours.scheduled", key: "scheduled", sortable: true, formatter: "formatScheduled" },
  { label: "assigned_video_tours.init_date", key: "init_date", sortable: true, formatter: "formatDateAssigned" },
  { label: "assigned_video_tours.end_date", key: "end_date", sortable: true, formatter: "formatDateAssigned" },
];

  if (
    showFilters &&
    (ability.can("update", "UserAssignedVideoTour") || ability.can("delete", "UserAssignedVideoTour"))
  ) {
    tableColumns.splice(0, 0, { label: "actions.name", key: "actions" });
  }

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];

  const searchQuery = ref("");
  const sortBy = ref("init_date");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const formatDateAssigned = (value) => {
    if (!value) return "-";
    return moment(value).format("DD/MM/YYYY HH:mm:ss");
  };

  const formatUser = (value) => {
    if (!value) return "-";
    const parts = [value?.name, value?.user, value?.email].filter(Boolean);
    return parts.join(" | ");
  };

  const formatScheduled = (value) => {
    return Number(value) === 1 ? "Programado" : "Cambio manual";
  };

  const parseTotal = (payload) => {
    if (payload?.meta && typeof payload.meta.total === "number") return payload.meta.total;
    if (typeof payload?.total === "number") return payload.total;
    if (Array.isArray(payload?.data)) return payload.data.length;
    return 0;
  };

  const fetchList = (ctx, callback) => {
    store
      .dispatch("user_assigned_video_tour/getAll", {
        //analyst_user_id: analystUserId,
        q: searchQuery.value,
        per_page: perPage.value,
        page: currentPage.value,
        order_by: sortBy.value,
        order_dir: isSortDirDesc.value ? "desc" : "asc",
        with: "analyst,supervisor,video_tours",

        supervisor_user_id: supervisorFilter.value || undefined,
        video_tour_id: videoTourFilter.value || undefined,
        scheduled:
          scheduledFilter.value === 0 || scheduledFilter.value === 1
            ? scheduledFilter.value
            : undefined,
      })
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        totalList.value = parseTotal(response);
        callback(rows);
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        totalList.value = 0;
        callback([]);
      });
  };

  const refetchData = () => {
    if (refListTable.value) refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => refetchData());

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  const filters = ref({});
  const filtersCount = useFilterCounter(filters);

  const buildFilters = () => {
    filters.value = {
      supervisor_user_id: supervisorFilter.value,
      video_tour_id: videoTourFilter.value,
      scheduled: scheduledFilter.value,
    };
    refetchData();
  };

  const clearSearchFilters = () => {
    supervisorFilter.value = null;
    videoTourFilter.value = null;
    scheduledFilter.value = null;
    buildFilters();
  };

  return {
    analystUserId,
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    formatUser,
    formatScheduled,
    refetchData,

    userOptions,
    videoTourOptions,

    supervisorFilter,
    videoTourFilter,
    scheduledFilter,
    filtersCount,
    buildFilters,
    clearSearchFilters,
  };
}
